/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Input, OnInit } from '@angular/core';
import { createDefaultGridModel } from '@gh/common-internal-shared';
import { TranslationService } from '@gh/core-mls';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableAutocompleteColumn,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  gridEditableValueCollectionComparator,
} from '@gh/core-ui-grid';
import {
  ExcludedCcRange,
  excludedCcRangeArr,
  excludedCcRangeCcComparator,
  excludedCcRangeFieldState,
  getExcludedCcRangeId,
  newExcludedCcRange,
  setExcludedCcRangeField,
  validateExcludedCcRangeField,
} from '../../../resources/model/system-vars.model';
import { SystemVarsReceivablesTabBusinessService } from '../../../resources/system-vars-receivables-tab-business.service';
import { SystemVarsReceivablesFormState } from '../system-vars-receivables-form.service';

@Component({
  selector: 'gh-system-vars-receivables-schedule-subtab',
  template: require('./system-vars-receivables-schedule-subtab.component.html'),
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
})
export class SystemVarsReceivablesScheduleSubtabComponent implements OnInit {
  @Input() focusedCell?: FocusedCell;

  definition: GridDefinition;

  constructor(private translations: TranslationService,
              private business: SystemVarsReceivablesTabBusinessService,
              public form: SystemVarsReceivablesFormState,
              private gridDefinitionFactory: GridDefinitionFactory) {
  }

  ngOnInit(): void {
    this.definition = this.buildGridDefinition();
  }

  onAdd(): void {
    this.form.setExcludedCostCenters(excludedCcRangeArr.prepend(this.form.excludedCostCenters, newExcludedCcRange()));
  }

  onDelete(row: ExcludedCcRange): void {
    this.form.setExcludedCostCenters(excludedCcRangeArr.remove(
      this.form.excludedCostCenters,
      getExcludedCcRangeId(row)));
  }

  onFocusedCellChange(): void {
    this.focusedCell = void 0;
  }

  private setEntityField(row: ExcludedCcRange, name: string, value: any): void {
    this.form.setExcludedCostCenters(excludedCcRangeArr.update(
      this.form.excludedCostCenters,
      getExcludedCcRangeId(row),
      setExcludedCcRangeField(row, name, value)));
  }

  private buildGridDefinition(): GridDefinition {
    const { editable } = this.form;
    const model = createDefaultGridModel({
      editable,
      getId: getExcludedCcRangeId,
      validate: validateExcludedCcRangeField,
      getCellDef: excludedCcRangeFieldState,
      command: (entity, name, value) => this.setEntityField(entity, name, value),
    });

    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [{
        sort: 'asc',
        field: 'from',
        width: 200,
        headerName: this.translations.get('lbl_from'),
        ...buildEditableAutocompleteColumn(
          {
            nameProperty: 'name',
            displayProperty: 'display',
            loadMore: (event) => event.load(this.business.findCostCentersByName(event.query)),
          },
          model.getCellAccessor('from')),
        comparator: gridEditableValueCollectionComparator(excludedCcRangeCcComparator),
      }, {
        field: 'to',
        width: 200,
        headerName: this.translations.get('lbl_to'),
        ...buildEditableAutocompleteColumn(
          {
            nameProperty: 'name',
            displayProperty: 'display',
            loadMore: (event, { data }) => event.load(this.business.findCostCentersByName(
              event.query,
              Number(data.from.name))),
          },
          model.getCellAccessor('to'),
          { useEditableAsEnabled: editable }),
        comparator: gridEditableValueCollectionComparator(excludedCcRangeCcComparator),
      }],
    });
  }
}
